import * as React from 'react';
import type { FieldPath, UseFormGetValues, UseFormUnregister } from 'react-hook-form';
import { type FieldValues, type UseControllerProps } from 'react-hook-form';

import { useEditingTableCellName } from 'af-root/hooks/useEditingTableCellName';

import type { OwnProps as PlainDropdownProps, DropdownOptionType } from 'af-components/Controls/Dropdown';

import EditModeCellWrapper from './EditModeCellWrapper';
import Dropdown from '../RHFFieldWrappers/Dropdown/Dropdown';
import DropdownField from '../RHFFieldWrappers/Dropdown/DropdownField';

type Props<
	D extends DropdownOptionType,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues,
> = {
	rhfId: number | string;
	onChange?: (name: string) => void;
	unregister: UseFormUnregister<TFieldValues>;
	getValues: UseFormGetValues<TFieldValues>;
	fieldsLength: number;
	readonly?: boolean;
	bold?: boolean;
} & UseControllerProps<TFieldValues, TName, TTransformedValues> & PlainDropdownProps<D>;

function DropdownCellRenderer<
	D extends DropdownOptionType,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues
>(props: Props<D, TFieldValues, TName, TTransformedValues>) {
	const { control, name, onChange, unregister, readonly, bold, placeholder } = props;

	const handleChange = React.useCallback(
		(exitEditMode: () => void) => () => {
			onChange?.(name);
			exitEditMode();
		}, [name, onChange]);

	const editingCellName = useEditingTableCellName();

	const returnComponent = React.useMemo(() => (
		<EditModeCellWrapper name={name} readonly={readonly}>
			{(shouldBeRegistered, exitEditMode) =>
				shouldBeRegistered ? (
					<DropdownField
						bold={bold}
						control={control}
						key={name}
						placeholder={placeholder}
						withCaret={true}
						{...props}
						onBlur={handleChange(exitEditMode)}
						onChange={handleChange(exitEditMode)}
						openFocused={editingCellName?.editingTableCellName === name ? true : false}
						unregister={unregister}
					/>
				) : (
					<Dropdown
						bold={bold}
						control={control}
						key={name}
						placeholder={placeholder}
						withCaret={true}
						{...props}
					/>
				)
			}
		</EditModeCellWrapper>
	), [bold, control, editingCellName?.editingTableCellName, handleChange, name, placeholder, props, readonly, unregister]);

	return returnComponent;
}

export default React.memo(DropdownCellRenderer) as typeof DropdownCellRenderer;
